import { Package, Plus, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CartItem, ItemVariant, POSItem } from '@/types';
import { getItemPrice, getLowestVariantPrice } from '@/utils/posItemPricing';
import { resolveFileUrl } from '@/utils/fileUrl';
import { formatMoney } from '@/utils/currency';
import { previewItemPromo, type PromotionCampaign } from '@/utils/promotions';
import { PosPromoPrice, PosSaleBadge } from '@/components/pos/PosSaleBadge';

function getCartQtyForItem(
  items: CartItem[],
  itemName: string,
  variant?: string,
) {
  return items.reduce((sum, line) => {
    if (line.item_type !== 'Product' || line.item !== itemName) return sum;
    if (variant !== undefined && (line.variant || '') !== variant) return sum;
    return sum + line.quantity;
  }, 0);
}

function stockBadgeClass(qty: number) {
  if (qty <= 0) return 'bg-red-50 text-red-700';
  if (qty <= 5) return 'bg-amber-50 text-amber-700';
  return 'bg-emerald-50 text-emerald-700';
}

function canAddProductQty(
  item: POSItem,
  cartItems: CartItem[],
  allowOos: boolean,
  addQty = 1,
  variant?: ItemVariant,
) {
  if (item.item_type !== 'Product' || allowOos) return true;
  const stock = variant ? (variant.stock_quantity ?? 0) : (item.stock_quantity ?? 0);
  const inCart = getCartQtyForItem(cartItems, item.name, variant?.name);
  return inCart + addQty <= stock;
}

interface PosImageGridViewProps {
  items: POSItem[];
  cartItems: CartItem[];
  allowOos: boolean;
  customerType: string;
  promotions?: PromotionCampaign[];
  onAddItem: (item: POSItem, variant?: ItemVariant) => void;
  onOpenVariantModal: (itemName: string) => void;
  variant?: 'default' | 'mobileCashier';
}

export default function PosImageGridView({
  items,
  cartItems,
  allowOos,
  customerType,
  promotions = [],
  onAddItem,
  onOpenVariantModal,
  variant = 'default',
}: PosImageGridViewProps) {
  const { t } = useTranslation();
  const isMobileCashier = variant === 'mobileCashier';

  const gridClass = isMobileCashier
    ? 'grid grid-cols-2 gap-2'
    : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3';

  const handleAdd = (item: POSItem, hasVariants: boolean, disabled: boolean) => {
    if (disabled) return;
    if (hasVariants) {
      onOpenVariantModal(item.name);
    } else {
      onAddItem(item);
    }
  };

  return (
    <div className={gridClass}>
      {items.map((item) => {
        const hasVariants = item.has_variants && (item.variants?.length ?? 0) > 0;
        const outOfStock = item.item_type === 'Product' && !allowOos && !item.has_variants && (item.stock_quantity ?? 0) <= 0;
        const cannotAddMore = item.item_type === 'Product' && !allowOos && !item.has_variants && !canAddProductQty(item, cartItems, allowOos);
        const variantsUnavailable = item.has_variants && (!item.variants || item.variants.length === 0);
        const disabled = outOfStock || cannotAddMore || variantsUnavailable;

        const displayPrice = hasVariants
          ? getLowestVariantPrice(item, customerType)
          : getItemPrice(item, customerType);
        const promo = hasVariants ? null : previewItemPromo(item, customerType, promotions);

        const priceLabel = variantsUnavailable
          ? t('pos.outOfStockShort')
          : outOfStock
            ? t('pos.outOfStockShort')
            : cannotAddMore
              ? t('pos.maxInCart')
              : hasVariants
                ? t('pos.fromPrice', { price: formatMoney(displayPrice) })
                : null;

        if (isMobileCashier) {
          return (
            <div
              key={item.name}
              className={`relative bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 ${
                disabled ? 'opacity-50' : ''
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  if (disabled) return;
                  if (hasVariants) onOpenVariantModal(item.name);
                }}
                disabled={disabled && !hasVariants}
                className={`w-full text-start ${hasVariants && !disabled ? 'cursor-pointer' : ''}`}
              >
                <div className="relative aspect-[5/4] bg-gray-100">
                  {item.image ? (
                    <img
                      src={resolveFileUrl(item.image)}
                      alt={item.item_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${
                      item.item_type === 'Product' ? 'bg-blue-50' : 'bg-purple-50'
                    }`}>
                      {item.item_type === 'Product'
                        ? <Package className="w-9 h-9 text-blue-400" />
                        : <Wrench className="w-9 h-9 text-purple-400" />
                      }
                    </div>
                  )}
                  {item.item_type === 'Product' && (
                    <span
                      className={`absolute top-1.5 start-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-sm ${stockBadgeClass(item.stock_quantity ?? 0)}`}
                    >
                      {(item.stock_quantity ?? 0).toLocaleString()}
                      {item.unit_of_measurement ? ` ${item.unit_of_measurement}` : ''}
                    </span>
                  )}
                  {promo && (
                    <span className="absolute top-1.5 end-1.5">
                      <PosSaleBadge preview={promo} />
                    </span>
                  )}
                </div>
                <div className="px-2 pt-1.5 pb-2 pe-10">
                  <h3 className="text-xs font-bold text-gray-900 line-clamp-2 leading-snug">
                    {item.item_name}
                  </h3>
                  <p className="text-sm font-semibold text-primary-600 mt-0.5">
                    {priceLabel ?? <PosPromoPrice price={displayPrice} preview={promo} />}
                  </p>
                </div>
              </button>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAdd(item, hasVariants, disabled);
                  }}
                  className="absolute bottom-2 end-2 w-9 h-9 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-md flex items-center justify-center transition-colors"
                  aria-label={t('common.add')}
                >
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                </button>
              )}
            </div>
          );
        }

        const handleClick = () => handleAdd(item, hasVariants, disabled);

        return (
          <button
            key={item.name}
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={`card overflow-hidden text-left transition-all ${
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-md hover:ring-2 hover:ring-primary-200 cursor-pointer'
            }`}
          >
            <div className="relative aspect-square bg-gray-100">
              {item.image ? (
                <img
                  src={resolveFileUrl(item.image)}
                  alt={item.item_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className={`w-full h-full flex items-center justify-center ${
                  item.item_type === 'Product' ? 'bg-blue-50' : 'bg-purple-50'
                }`}>
                  {item.item_type === 'Product'
                    ? <Package className="w-10 h-10 text-blue-400" />
                    : <Wrench className="w-10 h-10 text-purple-400" />
                  }
                </div>
              )}
              {item.item_type === 'Product' && (
                <span
                  className={`absolute top-1.5 right-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-sm ${stockBadgeClass(item.stock_quantity ?? 0)}`}
                >
                  {(item.stock_quantity ?? 0).toLocaleString()}
                </span>
              )}
              {promo && (
                <span className="absolute top-1.5 left-1.5">
                  <PosSaleBadge preview={promo} />
                </span>
              )}
            </div>
            <div className="p-2.5">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug min-h-[2.5rem]">
                {item.item_name}
              </h3>
              <p className="text-sm font-medium text-primary-700 mt-1">
                {priceLabel ?? <PosPromoPrice price={displayPrice} preview={promo} />}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
